import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ElementType,
  ReactNode,
} from 'react'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import { forwardRef, useContext } from 'react'
import cn from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import BrandingContext from '../context/BrandingContext'

type TButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

type TButtonColor = 'primary' | 'primary-invert' | 'white' | 'red-invert'

type Props = {
  children?: ReactNode
  block?: boolean
  as?: ElementType
  size?: TButtonSize
  color?: TButtonColor
  icon?: IconProp
  iconClassName?: string
  iconProps?: Omit<FontAwesomeIconProps, 'icon'>
  iconTrailing?: boolean
  className?: string
  to?: string
} & ButtonHTMLAttributes<HTMLButtonElement> &
  AnchorHTMLAttributes<HTMLAnchorElement>

const Button = forwardRef<unknown, Props>((props, ref) => {
  const {
    children,
    block,
    disabled,
    as: Component = 'button',
    size = 'md',
    color = 'white',
    type = 'button',
    icon,
    iconClassName = '',
    iconProps = {},
    iconTrailing = false,
    className,
    ...rest
  } = props

  const [branding] = useContext(BrandingContext)
  const { primaryColorPalette } = branding

  const buttonBaseCn = [
    'border',
    'inline-flex',
    'items-center',
    'font-medium',
    'shadow-xs',
    'appearance-none',
    'whitespace-nowrap',
    'focus:outline-hidden',
    'focus:ring-2',
    'focus:ring-offset-2',
  ]

  const buttonSizeCn: Record<TButtonSize, string[]> = {
    xs: ['px-2.5', 'py-1.5', 'text-xs', 'leading-4', 'rounded-sm'],
    sm: ['px-3', 'py-2', 'text-sm', 'leading-4', 'rounded-md'],
    md: ['px-4', 'py-2', 'text-sm', 'leading-5', 'rounded-md'],
    lg: ['px-4', 'py-2', 'text-base', 'leading-6', 'rounded-md'],
    xl: ['px-6', 'py-3', 'text-base', 'leading-6', 'rounded-md'],
  }

  const buttonColorCn: Record<TButtonColor, Record<string, boolean>> = {
    primary: {
      'border-transparent': true,
      'text-white': true,
      [`bg-${primaryColorPalette}-600`]: !disabled,
      [`hover:bg-${primaryColorPalette}-500`]: !disabled,
      [`focus:ring-${primaryColorPalette}-500`]: !disabled,
      [`active:bg-${primaryColorPalette}-700`]: !disabled,
      [`bg-${primaryColorPalette}-400`]: Boolean(disabled),
    },
    'primary-invert': {
      'border-transparent': true,
      [`bg-${primaryColorPalette}-100`]: !disabled,
      [`text-${primaryColorPalette}-700`]: !disabled,
      [`hover:bg-${primaryColorPalette}-50`]: !disabled,
      [`focus:ring-${primaryColorPalette}-500`]: !disabled,
      [`active:bg-${primaryColorPalette}-200`]: !disabled,
      [`bg-${primaryColorPalette}-50`]: Boolean(disabled),
      [`text-${primaryColorPalette}-500`]: Boolean(disabled),
    },
    white: {
      'border-gray-300': true,
      'bg-white': true,
      'text-gray-900': !disabled,
      'hover:bg-gray-50': !disabled,
      [`focus:ring-${primaryColorPalette}-500`]: !disabled,
      'active:text-gray-800': !disabled,
      'active:bg-gray-50': !disabled,
      'text-gray-500': Boolean(disabled),
    },
    'red-invert': {
      'border-gray-300': true,
      'bg-white': !disabled,
      'text-red-700': !disabled,
      'hover:bg-gray-50': !disabled,
      'focus:ring-red-500': !disabled,
      'active:bg-gray-50': !disabled,
      'text-red-500': Boolean(disabled),
    },
  }

  const iconColorCn: Record<TButtonColor, string> = {
    primary: 'text-white',
    'primary-invert': `text-${primaryColorPalette}-700`,
    white: 'text-gray-500',
    'red-invert': 'text-red-700',
  }

  const iconSizeCn: Record<TButtonSize, string[]> = {
    xs: ['h-4', 'w-4'],
    sm: ['h-4', 'w-4'],
    md: ['h-5', 'w-5'],
    lg: ['h-5', 'w-5'],
    xl: ['h-5', 'w-5'],
  }

  const iconLeadingCn: Record<TButtonSize, string[]> = {
    xs: ['-ml-0.5', 'mr-1'],
    sm: ['-ml-0.5', 'mr-2'],
    md: ['-ml-1', 'mr-2'],
    lg: ['-ml-1', 'mr-3'],
    xl: ['-ml-1', 'mr-3'],
  }

  const iconTrailingCn: Record<TButtonSize, string[]> = {
    xs: ['ml-1', '-mr-0.5'],
    sm: ['ml-2', '-mr-0.5'],
    md: ['ml-2', '-mr-1'],
    lg: ['ml-3', '-mr-1'],
    xl: ['ml-3', '-mr-1'],
  }

  const iconCn = cn(
    iconClassName,
    iconColorCn[color],
    iconSizeCn[size],
    children && (iconTrailing ? iconTrailingCn[size] : iconLeadingCn[size]),
  )

  const buttonCn = cn(
    className,
    buttonBaseCn,
    buttonSizeCn[size],
    buttonColorCn[color],
    {
      'cursor-not-allowed': disabled,
      'inline-flex': !block,
      'flex w-full justify-center': block,
    },
  )

  return (
    <Component
      className={buttonCn}
      type={type}
      disabled={disabled}
      ref={ref}
      {...rest}
    >
      {icon && !iconTrailing && (
        <FontAwesomeIcon className={iconCn} icon={icon} {...iconProps} />
      )}
      {children}
      {icon && iconTrailing && (
        <FontAwesomeIcon className={iconCn} icon={icon} {...iconProps} />
      )}
    </Component>
  )
})

export default Button
